/**
 * User-facing copy for the Buyer's Report web view (v2).
 *
 * Mirrors the strings in the backend PDF generator's copy.ts so the PDF and
 * web view stay in lockstep. When we change copy, change both.
 *
 * Voice mandate: gov.uk-adjacent, layman-accessible. See memory
 * `feedback-voice-mandate`. Hard rule 9 — every technical term gets a
 * layman gloss; "What this means" callouts are mandatory translations.
 */

export const HEADLINE = {
  introWhenAllClear:
    'No risk flags were raised by the public sources checked. This is not a guarantee that no issues exist; see Section 2 for what is covered.',
  bucketHigh: 'Higher attention',
  bucketWarning: 'Worth checking',
  bucketInfo: 'For information',
  introWhenFlagsRaised:
    'Risk flags grouped by attention level. Items in "Higher attention" should be discussed with your solicitor before exchange.',
  closing:
    'This report does not replace conveyancing searches. Discuss any items above with your solicitor before exchange.',
};

export const COVERAGE = {
  question: 'What does this report cover?',
  intro:
    'This report draws on public records held by named UK government and statutory bodies. Where a section returns no matching records, we record that as "No" rather than absence of information. Sources do not always reflect very recent changes.',
  closing:
    'A "Title summary" section (charges, restrictions, lease term remaining) is planned but is not yet checked because we have not integrated HM Land Registry. Your solicitor will obtain the current title register as part of conveyancing.',
};

export const EPC = {
  question: 'How energy-efficient is the property?',
  intro:
    'An Energy Performance Certificate (EPC) rates a property from A (most efficient) to G (least efficient). It is required when a property is sold or let. The figures below are taken from the most recent certificate on the public register.',
  noEpc:
    'No EPC was found on the public register for this property. This may mean the property has not been sold or let since EPCs became mandatory, or the certificate has not yet been lodged.',
  certificateExpiredHeading: 'This certificate has expired',
  certificateExpiredBody:
    'EPCs are valid for 10 years. A new EPC will be required before the property can next be sold or let.',
  belowMinimumHeading: 'Below the minimum letting standard',
  belowMinimumBody:
    'Properties rated F or G fall below the Minimum Energy Efficiency Standard (MEES) for new lettings. A landlord cannot grant a new tenancy without an exemption registered on the PRS Exemptions Register.',
  improvementsHeading: 'Recommended improvements',
  improvementsIntro:
    'Suggestions taken from the most recent certificate. Costs and savings are indicative; the actual figures depend on the property and current energy prices.',
  source:
    'Energy Performance of Buildings Register — Open Data Communities (Open Government Licence v3.0).',
};

export const FLOOD = {
  question: 'What is the flood risk?',
  intro:
    'Flood risk is recorded by the Environment Agency under two headings: rivers and sea (which is what mortgage lenders look at), and surface water (rainfall that pools or runs off). Both are summarised below.',
  riversHeading: 'Rivers and sea',
  surfaceHeading: 'Surface water',
  historicalHeading: 'Historical flood events',
  noHistoricalEvents: 'No historical flood events recorded for this location.',
  riversZoneLabel: {
    '1': 'Zone 1 — low risk',
    '2': 'Zone 2 — medium risk',
    '3a': 'Zone 3a — high risk',
    '3b': 'Zone 3b — functional floodplain',
    unknown: 'Not recorded',
  } as const,
  riversZoneExplanation: {
    '1': 'Less than 1-in-1,000 annual probability of flooding from rivers or the sea.',
    '2': 'Between 1-in-100 and 1-in-1,000 annual probability of flooding from rivers or the sea.',
    '3a': '1-in-100 or greater annual probability of flooding from rivers, or 1-in-200 from the sea.',
    '3b': 'Land that floods regularly and is reserved as a floodplain. Most development is restricted.',
    unknown: 'No flood-zone data was available for this address from the Environment Agency.',
  } as const,
  surfaceLabel: {
    very_low: 'Very low risk',
    low: 'Low risk',
    medium: 'Medium risk',
    high: 'High risk',
    unknown: 'Not recorded',
  } as const,
  surfaceExplanation: {
    very_low: 'Less than 1-in-1,000 annual chance of surface-water flooding.',
    low: 'Between 1-in-100 and 1-in-1,000 annual chance of surface-water flooding.',
    medium: 'Between 1-in-30 and 1-in-100 annual chance of surface-water flooding.',
    high: 'Greater than 1-in-30 annual chance of surface-water flooding.',
    unknown: 'No surface-water risk data was available for this address from the Environment Agency.',
  } as const,
  highRiskCalloutHeading: 'What flood risk means in practice',
  highRiskCalloutBody:
    'A property in Zone 2 or 3, or with a high surface-water risk, will usually need a Flood Risk Assessment for any planning application. Mortgage lenders may require specialist flood insurance, or apply additional conditions. Standard buildings insurance may exclude or surcharge flood cover.',
  source:
    'Environment Agency datasets via planning.data.gov.uk (Open Government Licence v3.0).',
};

export const HERITAGE = {
  question: 'Is the property protected by heritage designations?',
  intro:
    'A heritage designation is a formal listing recognising a building, area or feature as having historic or architectural value. Designations bring extra planning controls — what you can change, and what consent you need before changing it.',
  designationsHeading: 'Statutory designations',
  conservationHeading: 'Conservation area',
  noConservation: 'This property is not within a recorded conservation area.',
  inConservation: (name?: string) =>
    name
      ? `This property is within the ${name} conservation area.`
      : 'This property is within a recorded conservation area, though the name is not held in the public dataset.',
  listedCalloutHeading: (grade?: 'I' | 'II*' | 'II') =>
    grade ? `Listed building (Grade ${grade}) — what this means` : 'Listed building — what this means',
  listedCalloutBody: (grade?: 'I' | 'II*' | 'II') => {
    const gradeNote =
      grade === 'I'
        ? 'Grade I listings cover buildings of exceptional interest — only about 2.5% of listed buildings.'
        : grade === 'II*'
        ? 'Grade II* listings cover particularly important buildings of more than special interest.'
        : grade === 'II'
        ? 'Grade II is the most common listing, covering buildings of special interest.'
        : '';
    return [
      gradeNote,
      "Listed Building Consent is required for any work that affects the building's character, including some internal alterations. Carrying out works without consent is a criminal offence. You should expect a longer planning timeline and stricter scrutiny of materials, windows, and external changes.",
    ]
      .filter((s) => s)
      .join(' ');
  },
  conservationCalloutHeading: 'Conservation area — what this means',
  conservationCalloutBody:
    'Inside a conservation area, permitted development rights (the changes you can make without applying for planning permission) are reduced. Demolition, tree work, satellite dishes, cladding, and some external alterations need consent. Replacement windows often have to match the original style and materials.',
  source:
    'Historic England — National Heritage List for England (NHLE). Conservation area boundaries from planning.data.gov.uk (Open Government Licence v3.0).',
};

export const PLANNING = {
  question: 'Can the property be altered or extended?',
  intro:
    'Planning constraints are formal designations that affect what can be built, demolished or altered. The six checks below come from public planning datasets. A "No" means no matching record was found in the dataset; it does not always mean the constraint definitely does not apply.',
  permittedDevelopmentHeading: 'Permitted development rights',
  permittedDevelopmentClear: 'Standard permitted development rights apply.',
  permittedDevelopmentRestricted:
    'Permitted development rights are restricted by an Article 4 Direction (see below).',
  whatThisMeans: {
    green_belt: {
      heading: 'Green Belt — what this means',
      body: 'Green Belt land is protected against most new building. Extensions are usually limited to a modest proportion of the original size; new dwellings are very rarely permitted. Expect tight scrutiny on any application.',
    },
    aonb: {
      heading: 'AONB — what this means',
      body: 'In an Area of Outstanding Natural Beauty, planning policies prioritise conserving the landscape. Major development is only permitted in exceptional circumstances. Smaller works are still possible but expect closer review of scale and materials.',
    },
    national_park: {
      heading: 'National Park — what this means',
      body: 'National Parks have their own planning authority with stricter policies than the surrounding district. Conservation of landscape, wildlife and cultural heritage outweighs most other considerations.',
    },
    tpo: {
      heading: 'Tree Preservation Order — what this means',
      body: 'Specific trees or woodland at this location are protected. Written consent from the council is required before pruning, felling or any work to a protected tree. Carrying out unauthorised work is a criminal offence with fines up to £20,000 per tree.',
    },
    sssi: {
      heading: 'Site of Special Scientific Interest — what this means',
      body: 'The land is protected for its wildlife, geology or landforms. Most development that could damage the site needs consent from Natural England in addition to planning permission.',
    },
    article4: {
      heading: 'Article 4 Direction — what this means',
      body: 'An Article 4 Direction is a council order that removes some of the automatic permitted development rights. Works that would normally not need planning permission — replacing windows, cladding, satellite dishes, sometimes change of use — may require a formal application.',
    },
  } as const,
  source:
    'planning.data.gov.uk — Open Government Licence v3.0. Coverage may be incomplete; check with the local planning authority for the full picture.',
};

export const HISTORY = {
  question: 'What planning history is recorded for this property?',
  intro:
    'Planning applications recorded on planning.data.gov.uk for the address or its immediate vicinity. Coverage on this national platform is incomplete — many councils have not yet published their full back-catalogue, so this is a starting point rather than a full history.',
  coverageCalloutHeading: 'Coverage caveat',
  coverageCalloutBody:
    "planning.data.gov.uk receives applications from councils that have onboarded to the platform. If a council has not yet onboarded, or has only recently done so, older applications will not appear here. The local authority's own planning portal is the authoritative source.",
  noApplications:
    'No planning applications were found in the national dataset for this property.',
  source: 'planning.data.gov.uk — Open Government Licence v3.0. Coverage may be incomplete.',
};

export const TAX = {
  question: 'What tax and regulatory rules apply?',
  intro:
    'A summary of tax and regulatory rules that may apply to this property. Figures and exemptions change in budgets and Finance Acts; confirm with your solicitor or accountant before relying on them.',
  councilTaxHeading: 'Council tax band',
  councilTaxNotRecorded:
    'No council tax band recorded for this property in the public dataset.',
  councilTaxBody: (band: string) =>
    `The Valuation Office Agency records this property in council tax band ${band}. The annual amount depends on the local authority's rate, which changes each financial year.`,
  sdltHeading: 'Stamp Duty Land Tax (SDLT)',
  sdltIntro:
    'Stamp Duty Land Tax is paid by the buyer on completion. It is charged in bands. The figures below show the standard residential rates that would apply to this property — first-time-buyer relief and the additional-property surcharge are separate variants you should check with your solicitor.',
  section24Heading: 'Section 24 — landlord mortgage interest',
  section24Body:
    'If this property is bought to let, Section 24 of the Finance (No. 2) Act 2015 restricts the relief individual landlords can claim on mortgage interest. Relief is now given as a basic-rate (20%) tax credit rather than as a deduction from rental income. Owner-occupiers and limited companies are not affected.',
  leaseholdHeading: 'Leasehold and Freehold Reform Act 2024',
  leaseholdBody:
    'The Leasehold and Freehold Reform Act 2024 makes it easier for leaseholders to extend leases, buy the freehold, and challenge unfair charges. Some provisions are in force; others come in by secondary legislation. Whether they apply depends on the tenure of the property.',
  leaseholdTenureUnknown:
    'Whether these provisions apply depends on the tenure of the property. Tenure cannot be confirmed without title data — see Section 2.',
  leaseholdNotApplicable:
    'Not applicable — the property is not leasehold based on the title data available.',
  source:
    'UK statutory rules. SDLT bands HM Treasury / HMRC; Section 24 — Finance (No. 2) Act 2015; Leasehold and Freehold Reform Act 2024. No external lookup is performed.',
};

/**
 * Format a date value as DD Month YYYY (UK gov style).
 */
export function formatGovDate(value: string | Date | undefined | null): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Format a date+time as DD Month YYYY at HH:MM.
 */
export function formatGovDateTime(value: string | Date | undefined | null): string {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '—';
  const date = d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const time = d.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${date} at ${time}`;
}
